import type { ParseKeys } from 'i18next';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AutoHideScrollArea } from '@/components/AutoHideScrollArea';
import { Check } from '@/components/Icons/Check';
import { ChevronDown } from '@/components/Icons/ChevronDown';
import { SameLevelClassChips } from '@/pages/Overview/SameLevelClassChips';
import { useSameLevelClasses } from '@/pages/Overview/hooks/useSameLevelClasses';
import { tv } from '@/utils/tv';

interface Props {
  value: boolean;
  sameLevelClassIds: number[];
  onChange: (includeSameLevelClasses: boolean) => void;
  onSameLevelClassesChange: (sameLevelClassIds: number[]) => void;
}

const COMPARE_OPTIONS: Array<{
  includeSameLevelClasses: boolean;
  labelKey: ParseKeys;
}> = [
  {
    includeSameLevelClasses: false,
    labelKey: 'overview.ratings.compareClassmates',
  },
  {
    includeSameLevelClasses: true,
    labelKey: 'overview.ratings.compareParallel',
  },
];

const triggerChevron = tv({
  base: 'size-4 text-primary transition-transform duration-200',
  variants: {
    isOpen: {
      true: 'rotate-180',
      false: '',
    },
  },
});

const item = tv({
  base: 'flex cursor-pointer items-center justify-between gap-2 rounded-xl py-3 pr-2 pl-3 text-left text-subhead-14 hover:bg-hover',
  variants: {
    isSelected: {
      true: 'bg-info-surface text-primary',
      false: 'font-normal text-muted-foreground',
    },
  },
});

export const CompareChip: React.FC<Props> = ({
  value,
  sameLevelClassIds,
  onChange,
  onSameLevelClassesChange,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { data: sameLevelClasses } = useSameLevelClasses({
    enabled: isOpen && value,
  });

  const hasNoSameLevelClasses = sameLevelClasses?.length === 0;
  const selectedKey =
    COMPARE_OPTIONS.find((option) => option.includeSameLevelClasses === value)
      ?.labelKey ?? COMPARE_OPTIONS[0].labelKey;

  const handleSelect = (includeSameLevelClasses: boolean) => {
    setIsOpen(includeSameLevelClasses && !hasNoSameLevelClasses);
    onChange(includeSameLevelClasses);
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) {
        return;
      }
      setIsOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (value && hasNoSameLevelClasses) {
      setIsOpen(false);
    }
  }, [value, hasNoSameLevelClasses]);

  return (
    <div ref={containerRef} className="relative w-fit">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`flex h-8 cursor-pointer items-center gap-1 rounded-3xl px-2 py-0.5 shadow-card transition-colors ${isOpen ? 'bg-background' : 'bg-popover hover:bg-info-surface'}`}
      >
        <span className="px-1 text-caption-12 text-primary">
          {t(selectedKey)}
        </span>
        <ChevronDown className={triggerChevron({ isOpen })} />
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 z-10 mt-2 w-[240px] overflow-hidden rounded-3xl bg-card shadow-card">
          <AutoHideScrollArea
            viewportClassName="max-h-[524px] px-2 pt-2 pb-3"
            scrollbarClassName="w-[4px] pr-0"
          >
            <div className="flex flex-col gap-1">
              {COMPARE_OPTIONS.map((option) => {
                const isSelected = option.includeSameLevelClasses === value;
                return (
                  <button
                    key={option.labelKey}
                    type="button"
                    onClick={() => handleSelect(option.includeSameLevelClasses)}
                    className={item({ isSelected })}
                  >
                    {t(option.labelKey)}
                    {isSelected && <Check className="size-4 shrink-0" />}
                  </button>
                );
              })}
              <SameLevelClassChips
                isVisible={value}
                isAnimated={false}
                className="-mt-1 px-2"
                value={sameLevelClassIds}
                onChange={onSameLevelClassesChange}
              />
            </div>
          </AutoHideScrollArea>
        </div>
      )}
    </div>
  );
};
